import React, { useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { MlClassABadge, MlEstimateBadge, MlMetricLabel, MlSectionTitle } from '../../components/MlEstimateBadge';
import {
  getQualityColor,
  getSentimentColor,
  getSentimentLabel,
} from './helpers';

function swotItemText(item) {
  if (item && typeof item === 'object') return item.text || '';
  return item || '';
}

function SwotItemRow({ item }) {
  const text = swotItemText(item);
  const itemClass = item && typeof item === 'object' ? item.class : 'B';
  const basis = item && typeof item === 'object' ? item.basis : 'rule_based';
  return (
    <ListItem sx={{ py: 0.5, px: 0, alignItems: 'flex-start' }}>
      <ListItemText
        primary={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {text}
            </Typography>
            {itemClass === 'A' ? (
              <MlClassABadge />
            ) : (
              <MlEstimateBadge basis={basis || 'rule_based'} />
            )}
          </Box>
        }
      />
    </ListItem>
  );
}

export default function CompetitorDetailView({
  selectedClient,
  selectedCompetitor,
  competitorDetails,
  competitorPosts,
  competitorAnalytics,
  onBack,
}) {
  // Local tabs so parent ML Insights tab (Overview/Sentiment/etc.) is preserved on back.
  const [detailTab, setDetailTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            variant="outlined"
          >
            Back to Client View
          </Button>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            <Link
              color="inherit"
              onClick={onBack}
              sx={{ cursor: 'pointer' }}
            >
              {selectedClient?.name || 'Client'}
            </Link>
            <Typography color="text.primary">
              {selectedCompetitor.name}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <BusinessIcon /> {selectedCompetitor.name}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <MlMetricLabel basis="market_share">
                    <Typography variant="h6">Market Share</Typography>
                  </MlMetricLabel>
                </Box>
                <Typography variant="h4" color="primary">
                  {competitorDetails.marketShare?.market_share_percentage?.toFixed(
                    1
                  ) ||
                    (selectedCompetitor.market_share
                      ? parseFloat(selectedCompetitor.market_share).toFixed(1)
                      : '0.0')}
                  %
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(
                    100,
                    competitorDetails.marketShare?.market_share_percentage ||
                      (selectedCompetitor.market_share
                        ? parseFloat(selectedCompetitor.market_share) * 100
                        : 0)
                  )}
                  sx={{ mt: 2, height: 8, borderRadius: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BarChartIcon sx={{ mr: 1, color: 'success.main' }} />
                  <MlMetricLabel basis="quality_rubric">
                    <Typography variant="h6">Content Quality</Typography>
                  </MlMetricLabel>
                </Box>
                <Typography variant="h4" color="success.main">
                  {(
                    (competitorDetails.qualityScore?.content_quality_score ||
                      (selectedCompetitor.content_quality_score
                        ? parseFloat(selectedCompetitor.content_quality_score)
                        : 0)) * 100
                  ).toFixed(0)}
                  %
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    (competitorDetails.qualityScore?.content_quality_score ||
                      (selectedCompetitor.content_quality_score
                        ? parseFloat(selectedCompetitor.content_quality_score)
                        : 0)) * 100
                  }
                  color={getQualityColor(
                    competitorDetails.qualityScore?.content_quality_score ||
                      (selectedCompetitor.content_quality_score
                        ? parseFloat(selectedCompetitor.content_quality_score)
                        : 0)
                  )}
                  sx={{ mt: 2, height: 8, borderRadius: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PsychologyIcon
                    sx={{
                      mr: 1,
                      color:
                        getSentimentColor(
                          competitorDetails.sentiment?.brand_sentiment ||
                            (selectedCompetitor.brand_sentiment
                              ? parseFloat(selectedCompetitor.brand_sentiment)
                              : 0.5)
                        ) === 'success'
                          ? 'success.main'
                          : getSentimentColor(
                              competitorDetails.sentiment?.brand_sentiment ||
                                (selectedCompetitor.brand_sentiment
                                  ? parseFloat(
                                      selectedCompetitor.brand_sentiment
                                    )
                                  : 0.5)
                            ) === 'warning'
                          ? 'warning.main'
                          : 'error.main',
                    }}
                  />
                  <MlMetricLabel basis="sentiment_nlp">
                    <Typography variant="h6">Brand Sentiment</Typography>
                  </MlMetricLabel>
                </Box>
                <Typography
                  variant="h4"
                  color={getSentimentColor(
                    competitorDetails.sentiment?.brand_sentiment ||
                      (selectedCompetitor.brand_sentiment
                        ? parseFloat(selectedCompetitor.brand_sentiment)
                        : 0.5)
                  )}
                >
                  {getSentimentLabel(
                    competitorDetails.sentiment?.brand_sentiment ||
                      (selectedCompetitor.brand_sentiment
                        ? parseFloat(selectedCompetitor.brand_sentiment)
                        : 0.5)
                  )}
                </Typography>
                <Chip
                  label={
                    (
                      (competitorDetails.sentiment?.brand_sentiment ||
                        (selectedCompetitor.brand_sentiment
                          ? parseFloat(selectedCompetitor.brand_sentiment)
                          : 0.5)) * 100
                    ).toFixed(0) + '%'
                  }
                  color={getSentimentColor(
                    competitorDetails.sentiment?.brand_sentiment ||
                      (selectedCompetitor.brand_sentiment
                        ? parseFloat(selectedCompetitor.brand_sentiment)
                        : 0.5)
                  )}
                  size="small"
                  sx={{ mt: 1 }}
                />
                {(competitorDetails.sentiment?.model_name ||
                  competitorDetails.insights?.source) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {competitorDetails.sentiment?.model_name
                      ? `NLP model: ${competitorDetails.sentiment.model_name}`
                      : null}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Tabs
              value={detailTab}
              onChange={(e, v) => setDetailTab(v)}
              sx={{ mb: 2 }}
            >
              <Tab label="Posts" />
              <Tab label="Analytics" />
              <Tab label="Insights" />
            </Tabs>

            {detailTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Scraped Posts ({(Array.isArray(competitorPosts) ? competitorPosts : []).length})
                </Typography>
                {(Array.isArray(competitorPosts) ? competitorPosts : []).length === 0 ? (
                  <Alert severity="info">
                    No posts found. Run a Facebook / Instagram / YouTube scrape
                    (public metrics only). Empty Puppeteer responses no longer
                    count as a fresh scrape.
                  </Alert>
                ) : (
                  <>
                  <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
                    Public metrics only — likes/comments from public pages or the
                    YouTube API, not private Insights.
                  </Alert>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform</TableCell>
                          <TableCell>Content</TableCell>
                          <TableCell>Quality</TableCell>
                          <TableCell>CTA</TableCell>
                          <TableCell>Likes</TableCell>
                          <TableCell>Comments</TableCell>
                          <TableCell>Shares</TableCell>
                          <TableCell>Engagement</TableCell>
                          <TableCell>Scraped</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(competitorPosts) ? competitorPosts : []).slice(0, 20).map((post) => (
                          <TableRow key={post.id}>
                            <TableCell>
                              <Chip label={post.platform} size="small" />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 300 }}
                              >
                                {post.content || 'No content'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {post.content_quality_score != null
                                ? `${(
                                    parseFloat(post.content_quality_score || 0) *
                                    100
                                  ).toFixed(0)}%`
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={post.call_to_action ? 'Yes' : 'No'}
                                color={post.call_to_action ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{post.likes_count || 0}</TableCell>
                            <TableCell>{post.comments_count || 0}</TableCell>
                            <TableCell>{post.shares_count || 0}</TableCell>
                            <TableCell>
                              {(post.likes_count || 0) +
                                (post.comments_count || 0) +
                                (post.shares_count || 0)}
                            </TableCell>
                            <TableCell>
                              {new Date(post.scraped_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </Box>
            )}

            {detailTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Analytics Data ({(Array.isArray(competitorAnalytics) ? competitorAnalytics : []).length})
                </Typography>
                {(Array.isArray(competitorAnalytics) ? competitorAnalytics : []).length === 0 ? (
                  <Alert severity="info">No analytics data found.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform</TableCell>
                          <TableCell>Followers</TableCell>
                          <TableCell>Scraped At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(competitorAnalytics) ? competitorAnalytics : []).map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>
                              <Chip label={a.platform} size="small" />
                            </TableCell>
                            <TableCell>
                              {a.follower_count?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell>
                              {new Date(a.scraped_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {detailTab === 2 && (
              <Box>
                <MlSectionTitle sx={{ mb: 2 }}>
                  Competitive Insights
                </MlSectionTitle>
                {competitorDetails.insights ? (
                  <Stack spacing={2}>
                    <Alert severity="info" icon={false}>
                      Class A items cite scraped public metrics (engagement,
                      cadence, content_type mix, share). Class B items are
                      rubric / NLP / topic heuristics — estimate-badged. SWOT
                      refreshes when market share recomputes after scrape
                      batches.
                    </Alert>
                    {competitorDetails.insights.data_sufficiency &&
                      competitorDetails.insights.data_sufficiency !==
                        'sufficient' && (
                        <Alert severity="warning">
                          More data needed before SWOT lists appear —{' '}
                          {competitorDetails.insights.data_sufficiency.replace(
                            'needs_more_posts ',
                            ''
                          )}
                          . Stored aggregates above are still shown from scraped
                          data.
                        </Alert>
                      )}
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Stored posts
                        </Typography>
                        <Typography variant="h6">
                          {competitorDetails.insights.total_posts ?? 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Avg engagement / post
                        </Typography>
                        <Typography variant="h6">
                          {Number(
                            competitorDetails.insights.avg_engagement || 0
                          ).toFixed(0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Posts / week
                        </Typography>
                        <Typography variant="h6">
                          {Number(
                            competitorDetails.insights.posts_per_week || 0
                          ).toFixed(1)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Engagement share
                        </Typography>
                        <Typography variant="h6">
                          {competitorDetails.insights.market_share_percentage !=
                          null
                            ? `${Number(
                                competitorDetails.insights
                                  .market_share_percentage
                              ).toFixed(1)}%`
                            : '—'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {competitorDetails.insights.content_type_pct &&
                      Object.keys(competitorDetails.insights.content_type_pct)
                        .length > 0 && (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="subtitle2">
                              Content mix
                            </Typography>
                            <MlClassABadge />
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(
                              competitorDetails.insights.content_type_pct
                            ).map(([type, pct]) => (
                              <Chip
                                key={type}
                                size="small"
                                label={`${type}: ${pct}%`}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                    {(competitorDetails.insights.top_topics || []).length >
                      0 && (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="subtitle2">
                            Topic clusters
                          </Typography>
                          <MlEstimateBadge basis="pattern_analysis" />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {competitorDetails.insights.top_topics.map((t) => (
                            <Chip
                              key={t.topic}
                              size="small"
                              label={`${t.topic} (${t.post_count})`}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {(competitorDetails.insights.content_gaps || []).length >
                      0 && (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="subtitle2">
                            Content gaps (vs your posts)
                          </Typography>
                          <MlEstimateBadge basis="pattern_analysis" />
                        </Box>
                        <List dense disablePadding>
                          {competitorDetails.insights.content_gaps.map(
                            (gap, idx) => (
                              <SwotItemRow key={idx} item={gap} />
                            )
                          )}
                        </List>
                      </Box>
                    )}

                    {competitorDetails.insights.data_sufficiency ===
                      'sufficient' && (
                      <>
                        <Divider />
                        {[
                          {
                            key: 'strengths',
                            title: 'Strengths',
                            color: 'success.main',
                          },
                          {
                            key: 'weaknesses',
                            title: 'Weaknesses',
                            color: 'warning.main',
                          },
                          {
                            key: 'opportunities',
                            title: 'Opportunities',
                            color: 'info.main',
                          },
                          {
                            key: 'threats',
                            title: 'Threats',
                            color: 'error.main',
                          },
                          {
                            key: 'recommendations',
                            title: 'Recommendations',
                            color: 'primary.main',
                          },
                        ].map(({ key, title, color }) => {
                          const items = competitorDetails.insights[key] || [];
                          if (items.length === 0) return null;
                          return (
                            <Box key={key}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ color }}
                              >
                                {title}
                              </Typography>
                              <List dense disablePadding>
                                {items.map((item, idx) => (
                                  <SwotItemRow key={idx} item={item} />
                                ))}
                              </List>
                            </Box>
                          );
                        })}
                        {![
                          'strengths',
                          'weaknesses',
                          'opportunities',
                          'threats',
                          'recommendations',
                        ].some(
                          (k) =>
                            (competitorDetails.insights[k] || []).length > 0
                        ) && (
                          <Alert severity="info">
                            Not enough peer variance yet to derive SWOT lists
                            for this competitor.
                          </Alert>
                        )}
                      </>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="info">
                    No insights available for this competitor yet. Add scraped
                    posts and refresh to generate data-backed SWOT signals.
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
  );
}
